// =============================================================================
// Origin UI — src/pages/admin/LeaveAdminTab.tsx
// Leave Management admin tab for the Admin Config page (Phase 6).
// Sub-tabs: Leave Types, Entitlement Rules, Public Holidays, Working Week
// =============================================================================

import { useState, useEffect } from 'react';
import { api } from '@/services/apiService';
import { Card, ErrorBanner, LoadingSpinner, ConfirmDialog } from '@/components/common';
import styles from './admin.module.css';

// ─── Types ───

interface LeaveTypeItem {
  leaveTypeId: string; code: string; name: string; description: string | null;
  isPaid: boolean; requiresAttachment: boolean; attachmentRequiredAfterDays: number | null;
  allowHalfDay: boolean; allowCarryForward: boolean; maxCarryForwardDays: number | null;
  carryForwardExpiryMonths: number | null; defaultEntitlementDays: number | null;
  maxConsecutiveDays: number | null; requiresApproval: boolean; sortOrder: number; isActive: boolean;
}

interface EntitlementRuleItem {
  leaveEntitlementRuleId: string; leaveTypeId: string; leaveTypeName: string; leaveTypeCode: string;
  officeLocationId: number; officeName: string; minServiceYears: number; maxServiceYears: number | null;
  entitlementDays: number; accrualMethod: string; isActive: boolean;
}

interface HolidayItem {
  publicHolidayId: string; officeLocationId: number; officeName: string;
  year: number; date: string; name: string; isHalfDay: boolean;
  notes: string | null; isActive: boolean;
}

interface WorkingWeekItem {
  workingWeekPatternId: number; officeLocationId: number; officeName: string;
  dayOfWeek: number; dayName: string; isWorkingDay: boolean; isHalfDay: boolean;
}

interface OfficeItem { officeLocationId: number; name: string; isActive: boolean; }

type SubTab = 'holidays' | 'leaveTypes' | 'entitlements' | 'workingWeek' | 'carryForward' | 'adjustEntitlement' | 'extendedCalendar';

export function LeaveAdminTab() {
  const [subTab, setSubTab] = useState<SubTab>('holidays');

  return (
    <div>
      <div className={styles.subTabs}>
        {([
          ['holidays', 'Public Holidays'],
          ['leaveTypes', 'Leave Types'],
          ['entitlements', 'Entitlement Rules'],
          ['workingWeek', 'Working Week'],
          ['carryForward', 'Carry Forward'],
          ['adjustEntitlement', 'Adjust Entitlement'],
          ['extendedCalendar', 'Extended Calendar'],
        ] as [SubTab, string][]).map(([key, label]) => (
          <button
            key={key}
            className={`${styles.subTab} ${subTab === key ? styles.subTabActive : ''}`}
            onClick={() => setSubTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {subTab === 'holidays' && <HolidaysSubTab />}
      {subTab === 'leaveTypes' && <LeaveTypesSubTab />}
      {subTab === 'entitlements' && <EntitlementRulesSubTab />}
      {subTab === 'workingWeek' && <WorkingWeekSubTab />}
      {subTab === 'carryForward' && <CarryForwardSubTab />}
      {subTab === 'adjustEntitlement' && <AdjustEntitlementSubTab />}
      {subTab === 'extendedCalendar' && <ExtendedCalendarSubTab />}
    </div>
  );
}

// =============================================================================
// Public Holidays Sub-Tab
// =============================================================================

function HolidaysSubTab() {
  const [items, setItems] = useState<HolidayItem[]>([]);
  const [offices, setOffices] = useState<OfficeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editing, setEditing] = useState<HolidayItem | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [filterOffice, setFilterOffice] = useState<number | ''>('');
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [form, setForm] = useState({ officeLocationId: 0, year: new Date().getFullYear(), date: '', name: '', isHalfDay: false, notes: '', isActive: true });

  useEffect(() => { load(); }, [filterOffice, filterYear]);

  async function load() {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterOffice) params.set('officeLocationId', String(filterOffice));
      if (filterYear) params.set('year', String(filterYear));
      const [h, o] = await Promise.all([
        api.get<HolidayItem[]>(`/api/admin/leave/holidays?${params}`),
        api.get<OfficeItem[]>('/api/admin/office-locations'),
      ]);
      setItems(h);
      setOffices(o.filter(o => o.isActive));
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }

  function startCreate() {
    const officeId = filterOffice || offices[0]?.officeLocationId || 0;
    setForm({ officeLocationId: officeId, year: filterYear, date: '', name: '', isHalfDay: false, notes: '', isActive: true });
    setCreating(true); setEditing(null);
  }

  function startEdit(item: HolidayItem) {
    setForm({ officeLocationId: item.officeLocationId, year: item.year, date: item.date.split('T')[0], name: item.name, isHalfDay: item.isHalfDay, notes: item.notes || '', isActive: item.isActive });
    setEditing(item); setCreating(false);
  }

  async function save() {
    try {
      setError(''); setSuccess('');
      if (editing) {
        await api.put(`/api/admin/leave/holidays/${editing.publicHolidayId}`, form);
      } else {
        await api.post('/api/admin/leave/holidays', form);
      }
      setEditing(null); setCreating(false); await load();
      setSuccess(editing ? 'Holiday updated' : 'Holiday created');
    } catch (e: any) {
      const body = e.body ? JSON.parse(e.body) : null;
      setError(body?.message || body?.error || e.message);
    }
  }

  async function handleDelete() {
    if (!deleteId) return;
    try { await api.delete(`/api/admin/leave/holidays/${deleteId}`); setDeleteId(null); await load(); setSuccess('Holiday deleted'); }
    catch (e: any) { setError(e.message); setDeleteId(null); }
  }

  async function copyToNextYear() {
    try {
      setError(''); setSuccess('');
      const result = await api.post<{ copied: number; fromYear: number; toYear: number }>('/api/admin/leave/holidays/copy-year', {
        fromYear: filterYear,
        toYear: filterYear + 1,
        officeLocationId: filterOffice || null,
      });
      setSuccess(`Copied ${result.copied} holidays from ${result.fromYear} to ${result.toYear}. Please review and adjust dates.`);
    } catch (e: any) {
      const body = e.body ? JSON.parse(e.body) : null;
      setError(body?.message || body?.error || e.message);
    }
  }

  if (loading) return <LoadingSpinner />;

  // Group by office
  const grouped: Record<string, HolidayItem[]> = {};
  items.forEach(h => {
    const key = h.officeName;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(h);
  });

  return (
    <div>
      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}
      {success && <div style={{ padding: '10px 16px', marginBottom: 12, background: '#ECFDF5', border: '1px solid #10B981', borderRadius: 6, fontSize: 13, color: '#065F46' }}>{success}</div>}

      <div className={styles.toolBar}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span className={styles.count}>{items.length} holidays</span>
          <select className={styles.fieldInput} value={filterOffice} onChange={e => setFilterOffice(e.target.value ? Number(e.target.value) : '')} style={{ fontSize: 13, padding: '4px 28px 4px 8px' }}>
            <option value="">All offices</option>
            {offices.map(o => <option key={o.officeLocationId} value={o.officeLocationId}>{o.name}</option>)}
          </select>
          <select className={styles.fieldInput} value={filterYear} onChange={e => setFilterYear(Number(e.target.value))} style={{ fontSize: 13, padding: '4px 28px 4px 8px' }}>
            {[2025, 2026, 2027, 2028].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className={styles.addBtn} style={{ background: '#6B7280' }} onClick={copyToNextYear}>Copy to {filterYear + 1}</button>
          <button className={styles.addBtn} onClick={startCreate}>+ Add Holiday</button>
        </div>
      </div>

      {(creating || editing) && (
        <Card className={styles.formCard}>
          <h4 className={styles.formTitle}>{editing ? 'Edit Holiday' : 'New Holiday'}</h4>
          <div className={styles.formGrid}>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Office *</label>
              <select className={styles.fieldInput} value={form.officeLocationId} onChange={e => setForm({ ...form, officeLocationId: Number(e.target.value) })}>
                <option value={0}>Select office...</option>
                {offices.map(o => <option key={o.officeLocationId} value={o.officeLocationId}>{o.name}</option>)}
              </select>
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Date *</label>
              <input type="date" className={styles.fieldInput} value={form.date} onChange={e => { setForm({ ...form, date: e.target.value, year: new Date(e.target.value).getFullYear() }); }} />
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Holiday Name *</label>
              <input className={styles.fieldInput} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g., Eid Al Fitr" />
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Notes</label>
              <input className={styles.fieldInput} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="e.g., Approximate — moon sighting" />
            </div>
            <div className={styles.checkField}>
              <label><input type="checkbox" checked={form.isHalfDay} onChange={e => setForm({ ...form, isHalfDay: e.target.checked })} /> Half-day holiday</label>
            </div>
            <div className={styles.checkField}>
              <label><input type="checkbox" checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} /> Active</label>
            </div>
          </div>
          <div className={styles.formActions}>
            <button className={styles.cancelBtn} onClick={() => { setEditing(null); setCreating(false); }}>Cancel</button>
            <button className={styles.saveBtn} onClick={save}>Save</button>
          </div>
        </Card>
      )}

      {Object.entries(grouped).map(([officeName, holidays]) => (
        <div key={officeName} style={{ marginBottom: 16 }}>
          <Card padding={false}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--gray-200)', background: 'var(--gray-50)' }}>
              <strong style={{ color: 'var(--navy)', fontSize: 14 }}>{officeName}</strong>
              <span className={styles.muted} style={{ marginLeft: 8 }}>({holidays.length} holidays)</span>
            </div>
            <table className={styles.table}>
              <thead><tr><th>Date</th><th>Day</th><th>Holiday</th><th>Notes</th><th>Active</th><th></th></tr></thead>
              <tbody>
                {holidays.map(h => {
                  const date = new Date(h.date);
                  const dayName = date.toLocaleDateString('en-GB', { weekday: 'short' });
                  return (
                    <tr key={h.publicHolidayId}>
                      <td>{date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                      <td>{dayName}</td>
                      <td className={styles.bold}>{h.name}{h.isHalfDay && <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--orange)', fontWeight: 500 }}>½ day</span>}</td>
                      <td className={styles.muted}>{h.notes || '—'}</td>
                      <td><span className={h.isActive ? styles.active : styles.inactive}>{h.isActive ? 'Yes' : 'No'}</span></td>
                      <td>
                        <button className={styles.editBtn} onClick={() => startEdit(h)}>Edit</button>
                        <button className={styles.deleteBtn} onClick={() => setDeleteId(h.publicHolidayId)}>Delete</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        </div>
      ))}

      {items.length === 0 && !creating && (
        <Card><p className={styles.muted} style={{ textAlign: 'center', padding: 20 }}>No holidays found for this office/year combination.</p></Card>
      )}

      <ConfirmDialog open={deleteId !== null} title="Delete Holiday" message="Are you sure? This will remove the holiday." confirmLabel="Delete" confirmVariant="danger" onConfirm={handleDelete} onCancel={() => setDeleteId(null)} />
    </div>
  );
}

// =============================================================================
// Leave Types Sub-Tab
// =============================================================================

function LeaveTypesSubTab() {
  const [items, setItems] = useState<LeaveTypeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<LeaveTypeItem | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ code: '', name: '', description: '', isPaid: true, requiresAttachment: false, attachmentRequiredAfterDays: '', allowHalfDay: true, allowCarryForward: false, maxCarryForwardDays: '', carryForwardExpiryMonths: '', defaultEntitlementDays: '', maxConsecutiveDays: '', requiresApproval: true, sortOrder: '0', isActive: true });

  useEffect(() => { load(); }, []);

  async function load() {
    try { setLoading(true); setItems(await api.get('/api/admin/leave/types')); }
    catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }

  function startCreate() {
    setForm({ code: '', name: '', description: '', isPaid: true, requiresAttachment: false, attachmentRequiredAfterDays: '', allowHalfDay: true, allowCarryForward: false, maxCarryForwardDays: '', carryForwardExpiryMonths: '', defaultEntitlementDays: '', maxConsecutiveDays: '', requiresApproval: true, sortOrder: String(items.length + 1), isActive: true });
    setCreating(true); setEditing(null);
  }

  function startEdit(item: LeaveTypeItem) {
    setForm({ code: item.code, name: item.name, description: item.description || '', isPaid: item.isPaid, requiresAttachment: item.requiresAttachment, attachmentRequiredAfterDays: item.attachmentRequiredAfterDays != null ? String(item.attachmentRequiredAfterDays) : '', allowHalfDay: item.allowHalfDay, allowCarryForward: item.allowCarryForward, maxCarryForwardDays: item.maxCarryForwardDays != null ? String(item.maxCarryForwardDays) : '', carryForwardExpiryMonths: item.carryForwardExpiryMonths != null ? String(item.carryForwardExpiryMonths) : '', defaultEntitlementDays: item.defaultEntitlementDays != null ? String(item.defaultEntitlementDays) : '', maxConsecutiveDays: item.maxConsecutiveDays != null ? String(item.maxConsecutiveDays) : '', requiresApproval: item.requiresApproval, sortOrder: String(item.sortOrder), isActive: item.isActive });
    setEditing(item); setCreating(false);
  }

  async function save() {
    try {
      setError('');
      const payload = { code: form.code, name: form.name, description: form.description || null, isPaid: form.isPaid, requiresAttachment: form.requiresAttachment, attachmentRequiredAfterDays: form.attachmentRequiredAfterDays ? Number(form.attachmentRequiredAfterDays) : null, allowHalfDay: form.allowHalfDay, allowCarryForward: form.allowCarryForward, maxCarryForwardDays: form.maxCarryForwardDays ? Number(form.maxCarryForwardDays) : null, carryForwardExpiryMonths: form.carryForwardExpiryMonths ? Number(form.carryForwardExpiryMonths) : null, defaultEntitlementDays: form.defaultEntitlementDays ? Number(form.defaultEntitlementDays) : null, maxConsecutiveDays: form.maxConsecutiveDays ? Number(form.maxConsecutiveDays) : null, requiresApproval: form.requiresApproval, sortOrder: Number(form.sortOrder), isActive: form.isActive };
      if (editing) { await api.put(`/api/admin/leave/types/${editing.leaveTypeId}`, payload); }
      else { await api.post('/api/admin/leave/types', payload); }
      setEditing(null); setCreating(false); await load();
    } catch (e: any) { const body = e.body ? JSON.parse(e.body) : null; setError(body?.message || body?.error || e.message); }
  }

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}
      <div className={styles.toolBar}>
        <span className={styles.count}>{items.length} leave types</span>
        <button className={styles.addBtn} onClick={startCreate}>+ Add Leave Type</button>
      </div>

      {(creating || editing) && (
        <Card className={styles.formCard}>
          <h4 className={styles.formTitle}>{editing ? 'Edit Leave Type' : 'New Leave Type'}</h4>
          <div className={styles.formGrid}>
            <div className={styles.field}><label className={styles.fieldLabel}>Code *</label><input className={styles.fieldInput} value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} placeholder="e.g., ANN" maxLength={20} /></div>
            <div className={styles.field}><label className={styles.fieldLabel}>Name *</label><input className={styles.fieldInput} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g., Annual Leave" /></div>
            <div className={styles.field}><label className={styles.fieldLabel}>Description</label><input className={styles.fieldInput} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
            <div className={styles.field}><label className={styles.fieldLabel}>Default Entitlement (days)</label><input type="number" className={styles.fieldInput} value={form.defaultEntitlementDays} onChange={e => setForm({ ...form, defaultEntitlementDays: e.target.value })} placeholder="Leave blank if no default" /></div>
            <div className={styles.field}><label className={styles.fieldLabel}>Sort Order</label><input type="number" className={styles.fieldInput} value={form.sortOrder} onChange={e => setForm({ ...form, sortOrder: e.target.value })} /></div>
            <div className={styles.field}><label className={styles.fieldLabel}>Max Carry Forward (days)</label><input type="number" className={styles.fieldInput} value={form.maxCarryForwardDays} onChange={e => setForm({ ...form, maxCarryForwardDays: e.target.value })} placeholder="Blank = no cap" /></div>
            <div className={styles.checkField}><label><input type="checkbox" checked={form.isPaid} onChange={e => setForm({ ...form, isPaid: e.target.checked })} /> Paid leave</label></div>
            <div className={styles.checkField}><label><input type="checkbox" checked={form.allowHalfDay} onChange={e => setForm({ ...form, allowHalfDay: e.target.checked })} /> Allow half-day</label></div>
            <div className={styles.checkField}><label><input type="checkbox" checked={form.allowCarryForward} onChange={e => setForm({ ...form, allowCarryForward: e.target.checked })} /> Allow carry forward</label></div>
            <div className={styles.checkField}><label><input type="checkbox" checked={form.requiresAttachment} onChange={e => setForm({ ...form, requiresAttachment: e.target.checked })} /> Requires attachment</label></div>
            <div className={styles.checkField}><label><input type="checkbox" checked={form.requiresApproval} onChange={e => setForm({ ...form, requiresApproval: e.target.checked })} /> Requires approval</label></div>
            <div className={styles.checkField}><label><input type="checkbox" checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} /> Active</label></div>
          </div>
          <div className={styles.formActions}>
            <button className={styles.cancelBtn} onClick={() => { setEditing(null); setCreating(false); }}>Cancel</button>
            <button className={styles.saveBtn} onClick={save}>Save</button>
          </div>
        </Card>
      )}

      <Card padding={false}>
        <table className={styles.table}>
          <thead><tr><th>Code</th><th>Name</th><th>Paid</th><th>Half-day</th><th>Carry Fwd</th><th>Default Days</th><th>Active</th><th></th></tr></thead>
          <tbody>
            {items.map(item => (
              <tr key={item.leaveTypeId}>
                <td><span className={styles.mono}>{item.code}</span></td>
                <td className={styles.bold}>{item.name}</td>
                <td>{item.isPaid ? 'Yes' : 'No'}</td>
                <td>{item.allowHalfDay ? 'Yes' : 'No'}</td>
                <td>{item.allowCarryForward ? `Yes (${item.maxCarryForwardDays ?? '∞'})` : 'No'}</td>
                <td>{item.defaultEntitlementDays ?? '—'}</td>
                <td><span className={item.isActive ? styles.active : styles.inactive}>{item.isActive ? 'Yes' : 'No'}</span></td>
                <td><button className={styles.editBtn} onClick={() => startEdit(item)}>Edit</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// =============================================================================
// Entitlement Rules Sub-Tab
// =============================================================================

function EntitlementRulesSubTab() {
  const [items, setItems] = useState<EntitlementRuleItem[]>([]);
  const [leaveTypes, setLeaveTypes] = useState<LeaveTypeItem[]>([]);
  const [offices, setOffices] = useState<OfficeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<EntitlementRuleItem | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [form, setForm] = useState({ leaveTypeId: '', officeLocationId: 0, minServiceYears: 0, maxServiceYears: '', entitlementDays: 0, accrualMethod: 'Annual', isActive: true });

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      setLoading(true);
      const [r, t, o] = await Promise.all([
        api.get<EntitlementRuleItem[]>('/api/admin/leave/entitlement-rules'),
        api.get<LeaveTypeItem[]>('/api/admin/leave/types'),
        api.get<OfficeItem[]>('/api/admin/office-locations'),
      ]);
      setItems(r); setLeaveTypes(t); setOffices(o.filter(o => o.isActive));
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }

  function startCreate() {
    setForm({ leaveTypeId: leaveTypes[0]?.leaveTypeId || '', officeLocationId: offices[0]?.officeLocationId || 0, minServiceYears: 0, maxServiceYears: '', entitlementDays: 30, accrualMethod: 'Annual', isActive: true });
    setCreating(true); setEditing(null);
  }

  function startEdit(item: EntitlementRuleItem) {
    setForm({ leaveTypeId: item.leaveTypeId, officeLocationId: item.officeLocationId, minServiceYears: item.minServiceYears, maxServiceYears: item.maxServiceYears != null ? String(item.maxServiceYears) : '', entitlementDays: item.entitlementDays, accrualMethod: item.accrualMethod, isActive: item.isActive });
    setEditing(item); setCreating(false);
  }

  async function save() {
    try {
      setError('');
      const payload = { ...form, maxServiceYears: form.maxServiceYears ? Number(form.maxServiceYears) : null };
      if (editing) { await api.put(`/api/admin/leave/entitlement-rules/${editing.leaveEntitlementRuleId}`, payload); }
      else { await api.post('/api/admin/leave/entitlement-rules', payload); }
      setEditing(null); setCreating(false); await load();
    } catch (e: any) { const body = e.body ? JSON.parse(e.body) : null; setError(body?.message || body?.error || e.message); }
  }

  async function handleDelete() {
    if (!deleteId) return;
    try { await api.delete(`/api/admin/leave/entitlement-rules/${deleteId}`); setDeleteId(null); await load(); }
    catch (e: any) { setError(e.message); setDeleteId(null); }
  }

  if (loading) return <LoadingSpinner />;

  // Group by leave type
  const grouped: Record<string, EntitlementRuleItem[]> = {};
  items.forEach(r => {
    const key = `${r.leaveTypeCode} — ${r.leaveTypeName}`;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(r);
  });

  return (
    <div>
      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}
      <div className={styles.toolBar}>
        <span className={styles.count}>{items.length} entitlement rules</span>
        <button className={styles.addBtn} onClick={startCreate}>+ Add Rule</button>
      </div>

      {(creating || editing) && (
        <Card className={styles.formCard}>
          <h4 className={styles.formTitle}>{editing ? 'Edit Entitlement Rule' : 'New Entitlement Rule'}</h4>
          <div className={styles.formGrid}>
            <div className={styles.field}><label className={styles.fieldLabel}>Leave Type *</label><select className={styles.fieldInput} value={form.leaveTypeId} onChange={e => setForm({ ...form, leaveTypeId: e.target.value })}>{leaveTypes.map(t => <option key={t.leaveTypeId} value={t.leaveTypeId}>{t.code} — {t.name}</option>)}</select></div>
            <div className={styles.field}><label className={styles.fieldLabel}>Office *</label><select className={styles.fieldInput} value={form.officeLocationId} onChange={e => setForm({ ...form, officeLocationId: Number(e.target.value) })}>{offices.map(o => <option key={o.officeLocationId} value={o.officeLocationId}>{o.name}</option>)}</select></div>
            <div className={styles.field}><label className={styles.fieldLabel}>Entitlement Days *</label><input type="number" className={styles.fieldInput} value={form.entitlementDays} onChange={e => setForm({ ...form, entitlementDays: Number(e.target.value) })} /></div>
            <div className={styles.field}><label className={styles.fieldLabel}>Min Service Years</label><input type="number" className={styles.fieldInput} value={form.minServiceYears} onChange={e => setForm({ ...form, minServiceYears: Number(e.target.value) })} /></div>
            <div className={styles.field}><label className={styles.fieldLabel}>Max Service Years</label><input type="number" className={styles.fieldInput} value={form.maxServiceYears} onChange={e => setForm({ ...form, maxServiceYears: e.target.value })} placeholder="Blank = no upper limit" /></div>
            <div className={styles.field}><label className={styles.fieldLabel}>Accrual Method</label><select className={styles.fieldInput} value={form.accrualMethod} onChange={e => setForm({ ...form, accrualMethod: e.target.value })}><option value="Annual">Annual (full upfront)</option><option value="Monthly">Monthly accrual</option></select></div>
            <div className={styles.checkField}><label><input type="checkbox" checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} /> Active</label></div>
          </div>
          <div className={styles.formActions}>
            <button className={styles.cancelBtn} onClick={() => { setEditing(null); setCreating(false); }}>Cancel</button>
            <button className={styles.saveBtn} onClick={save}>Save</button>
          </div>
        </Card>
      )}

      {Object.entries(grouped).map(([label, rules]) => (
        <div key={label} style={{ marginBottom: 16 }}>
          <Card padding={false}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--gray-200)', background: 'var(--gray-50)' }}>
              <strong style={{ color: 'var(--navy)', fontSize: 14 }}>{label}</strong>
            </div>
            <table className={styles.table}>
              <thead><tr><th>Office</th><th>Service Years</th><th>Days</th><th>Accrual</th><th>Active</th><th></th></tr></thead>
              <tbody>
                {rules.map(r => (
                  <tr key={r.leaveEntitlementRuleId}>
                    <td className={styles.bold}>{r.officeName}</td>
                    <td>{r.minServiceYears}+ {r.maxServiceYears != null ? `to ${r.maxServiceYears}` : '(no limit)'}</td>
                    <td style={{ fontWeight: 700, color: 'var(--turquoise-dark)' }}>{r.entitlementDays}</td>
                    <td>{r.accrualMethod}</td>
                    <td><span className={r.isActive ? styles.active : styles.inactive}>{r.isActive ? 'Yes' : 'No'}</span></td>
                    <td>
                      <button className={styles.editBtn} onClick={() => startEdit(r)}>Edit</button>
                      <button className={styles.deleteBtn} onClick={() => setDeleteId(r.leaveEntitlementRuleId)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      ))}

      <ConfirmDialog open={deleteId !== null} title="Delete Entitlement Rule" message="Are you sure?" confirmLabel="Delete" confirmVariant="danger" onConfirm={handleDelete} onCancel={() => setDeleteId(null)} />
    </div>
  );
}

// =============================================================================
// Working Week Sub-Tab
// =============================================================================

function WorkingWeekSubTab() {
  const [items, setItems] = useState<WorkingWeekItem[]>([]);
  const [offices, setOffices] = useState<OfficeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedOffice, setSelectedOffice] = useState<number>(0);
  const [editDays, setEditDays] = useState<{ dayOfWeek: number; isWorkingDay: boolean; isHalfDay: boolean }[]>([]);

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      setLoading(true);
      const [w, o] = await Promise.all([
        api.get<WorkingWeekItem[]>('/api/admin/leave/working-week'),
        api.get<OfficeItem[]>('/api/admin/office-locations'),
      ]);
      setItems(w);
      const activeOffices = o.filter(o => o.isActive);
      setOffices(activeOffices);
      if (!selectedOffice && activeOffices.length > 0) setSelectedOffice(activeOffices[0].officeLocationId);
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }

  useEffect(() => {
    if (selectedOffice) {
      const officePatterns = items.filter(i => i.officeLocationId === selectedOffice);
      const days = [0,1,2,3,4,5,6].map(d => {
        const existing = officePatterns.find(p => p.dayOfWeek === d);
        return { dayOfWeek: d, isWorkingDay: existing?.isWorkingDay ?? false, isHalfDay: existing?.isHalfDay ?? false };
      });
      setEditDays(days);
    }
  }, [selectedOffice, items]);

  async function saveWeek() {
    try {
      setError(''); setSuccess('');
      await api.put(`/api/admin/leave/working-week/${selectedOffice}`, { days: editDays });
      setSuccess('Working week updated');
      await load();
    } catch (e: any) { setError(e.message); }
  }

  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}
      {success && <div style={{ padding: '10px 16px', marginBottom: 12, background: '#ECFDF5', border: '1px solid #10B981', borderRadius: 6, fontSize: 13, color: '#065F46' }}>{success}</div>}

      <div className={styles.toolBar}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span className={styles.count}>Working week patterns</span>
          <select className={styles.fieldInput} value={selectedOffice} onChange={e => setSelectedOffice(Number(e.target.value))} style={{ fontSize: 13, padding: '4px 28px 4px 8px' }}>
            {offices.map(o => <option key={o.officeLocationId} value={o.officeLocationId}>{o.name}</option>)}
          </select>
        </div>
        <button className={styles.saveBtn} onClick={saveWeek}>Save Changes</button>
      </div>

      <Card padding={false}>
        <table className={styles.table}>
          <thead><tr><th>Day</th><th>Working Day</th><th>Half Day</th></tr></thead>
          <tbody>
            {editDays.map((d, i) => (
              <tr key={d.dayOfWeek} style={{ background: d.isWorkingDay ? 'white' : 'var(--gray-50)' }}>
                <td className={styles.bold}>{dayNames[d.dayOfWeek]}</td>
                <td>
                  <input type="checkbox" checked={d.isWorkingDay} onChange={e => {
                    const updated = [...editDays];
                    updated[i] = { ...updated[i], isWorkingDay: e.target.checked, isHalfDay: e.target.checked ? updated[i].isHalfDay : false };
                    setEditDays(updated);
                  }} />
                </td>
                <td>
                  <input type="checkbox" checked={d.isHalfDay} disabled={!d.isWorkingDay} onChange={e => {
                    const updated = [...editDays];
                    updated[i] = { ...updated[i], isHalfDay: e.target.checked };
                    setEditDays(updated);
                  }} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// =============================================================================
// Carry Forward Sub-Tab — Manual entry for opening balances
// =============================================================================

function CarryForwardSubTab() {
  const [employees, setEmployees] = useState<any[]>([]);
  const [leaveTypes, setLeaveTypes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [selectedEmp, setSelectedEmp] = useState<any | null>(null);
  const [form, setForm] = useState({ leaveTypeId: '', fromYear: 2025, daysCarriedForward: 0, notes: '' });
  const [existing, setExisting] = useState<any[]>([]);

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      setLoading(true);
      const [e, t] = await Promise.all([
        api.get<any[]>('/api/employees'),
        api.get<any[]>('/api/admin/leave/types'),
      ]);
      setEmployees(e);
      setLeaveTypes(t.filter((lt: any) => lt.isActive && lt.allowCarryForward));
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }

  async function selectEmployee(emp: any) {
    setSelectedEmp(emp);
    setForm({ ...form, leaveTypeId: leaveTypes[0]?.leaveTypeId || '' });
    // Load existing carry-forward records for this employee
    try {
      const balances = await api.get<any[]>(`/api/leave/team/balance/${emp.employeeId}`);
      setExisting(balances.filter((b: any) => b.carryForward > 0));
    } catch { setExisting([]); }
  }

  async function saveCarryForward() {
    if (!selectedEmp || !form.leaveTypeId || form.daysCarriedForward <= 0) {
      setError('Please select an employee, leave type, and enter days > 0');
      return;
    }
    try {
      setError(''); setSuccess('');
      // Create via the employee override endpoint as a carry-forward
      // Actually, we need to create a LeaveBalanceCarryForward record
      // The admin endpoint for this is POST /api/admin/leave/carry-forward/manual
      // But that doesn't exist yet — let's use the existing carry-forward process endpoint
      // or create the record directly via employee entitlement override

      // For now, use the carry-forward table directly via a new simple endpoint
      // We'll POST to a generic endpoint
      await api.post('/api/admin/leave/carry-forward/manual', {
        employeeId: selectedEmp.employeeId,
        leaveTypeId: form.leaveTypeId,
        fromYear: form.fromYear,
        toYear: form.fromYear + 1,
        daysCarriedForward: form.daysCarriedForward,
        notes: form.notes,
      });
      setSuccess(`Carry-forward of ${form.daysCarriedForward} days saved for ${selectedEmp.firstName} ${selectedEmp.lastName}`);
      setForm({ ...form, daysCarriedForward: 0, notes: '' });
      await selectEmployee(selectedEmp); // Refresh
    } catch (e: any) {
      const body = e.body ? JSON.parse(e.body) : null;
      setError(body?.message || body?.error || e.message || 'Failed to save');
    }
  }

  if (loading) return <LoadingSpinner />;

  const filtered = search
    ? employees.filter((e: any) => {
        const name = `${e.firstName} ${e.lastName} ${e.employeeNumber}`.toLowerCase();
        return name.includes(search.toLowerCase());
      })
    : [];


  return (
    <div>
      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}
      {success && <div style={{ padding: '10px 16px', marginBottom: 12, background: '#ECFDF5', border: '1px solid #10B981', borderRadius: 6, fontSize: 13, color: '#065F46' }}>{success}</div>}

      <p style={{ fontSize: 13, color: 'var(--gray-500)', marginBottom: 16 }}>
        Use this to enter opening carry-forward balances (e.g., days carried from 2025 into 2026). 
        Search for an employee, select a leave type, and enter the number of days to carry forward.
      </p>

      {/* Employee search */}
      <div className={styles.toolBar}>
        <div style={{ flex: 1 }}>
          <input
            className={styles.fieldInput}
            placeholder="Search employee by name or number..."
            value={search}
            onChange={e => { setSearch(e.target.value); setSelectedEmp(null); }}
            style={{ width: '100%' }}
          />
          {search && filtered.length > 0 && !selectedEmp && (
            <Card className={styles.formCard} padding={false}>
              <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                {filtered.slice(0, 10).map((emp: any) => (
                  <div
                    key={emp.employeeId}
                    style={{ padding: '8px 16px', cursor: 'pointer', borderBottom: '1px solid var(--gray-100)' }}
                    onClick={() => { selectEmployee(emp); setSearch(`${emp.firstName} ${emp.lastName} (${emp.employeeNumber})`); }}
                  >
                    <strong>{emp.firstName} {emp.lastName}</strong> <span style={{ color: 'var(--gray-400)' }}>{emp.employeeNumber} — {emp.departmentName}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Entry form — shown when employee selected */}
      {selectedEmp && (
        <Card className={styles.formCard}>
          <h4 className={styles.formTitle}>Add Carry Forward for {selectedEmp.firstName} {selectedEmp.lastName} ({selectedEmp.employeeNumber})</h4>
          
          {existing.length > 0 && (
            <div style={{ marginBottom: 16, padding: 12, background: 'var(--gray-50)', borderRadius: 6, fontSize: 13 }}>
              <strong>Current carry-forward:</strong>
              {existing.map((b: any, i: number) => (
                <span key={i}> {b.leaveTypeName}: {b.carryForward} days{i < existing.length - 1 ? ',' : ''}</span>
              ))}
            </div>
          )}

          <div className={styles.formGrid}>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Leave Type *</label>
              <select className={styles.fieldInput} value={form.leaveTypeId} onChange={e => setForm({ ...form, leaveTypeId: e.target.value })}>
                <option value="">Select...</option>
                {leaveTypes.map((lt: any) => <option key={lt.leaveTypeId} value={lt.leaveTypeId}>{lt.code} — {lt.name}</option>)}
              </select>
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>From Year</label>
              <select className={styles.fieldInput} value={form.fromYear} onChange={e => setForm({ ...form, fromYear: Number(e.target.value) })}>
                <option value={2024}>2024 → 2025</option>
                <option value={2025}>2025 → 2026</option>
                <option value={2026}>2026 → 2027</option>
              </select>
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Days Carried Forward *</label>
              <input type="number" className={styles.fieldInput} value={form.daysCarriedForward} onChange={e => setForm({ ...form, daysCarriedForward: Number(e.target.value) })} min="0" max="30" step="0.5" />
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Notes</label>
              <input className={styles.fieldInput} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="e.g., Opening balance from previous system" />
            </div>
          </div>
          <div className={styles.formActions}>
            <button className={styles.cancelBtn} onClick={() => { setSelectedEmp(null); setSearch(''); }}>Cancel</button>
            <button className={styles.saveBtn} onClick={saveCarryForward}>Save Carry Forward</button>
          </div>
        </Card>
      )}
    </div>
  );
}


// =============================================================================
// Adjust Entitlement Sub-Tab — Credit TOIL / adjust entitlement for any leave type
// =============================================================================

function AdjustEntitlementSubTab() {
  const [employees, setEmployees] = useState<any[]>([]);
  const [leaveTypes, setLeaveTypes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [selectedEmp, setSelectedEmp] = useState<any | null>(null);
  const currentYear = new Date().getFullYear();
  const [form, setForm] = useState({ leaveTypeId: '', year: currentYear, entitlementDays: 0, notes: '' });
  const [existing, setExisting] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);
  const [allOverrides, setAllOverrides] = useState<any[]>([]);
  const [filterType, setFilterType] = useState('TOIL');

  useEffect(() => { load(); }, []);

  async function loadAllOverrides() {
    try {
      const ov = await api.get<any[]>(`/api/admin/leave/employee-overrides?year=${currentYear}`);
      setAllOverrides(ov);
    } catch { setAllOverrides([]); }
  }

  async function load() {
    try {
      setLoading(true);
      const [e, t] = await Promise.all([
        api.get<any[]>('/api/employees'),
        api.get<any[]>('/api/admin/leave/types'),
      ]);
      setEmployees(e);
      setLeaveTypes(t.filter((lt: any) => lt.isActive));
      await loadAllOverrides();
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }

  async function selectEmployee(emp: any) {
    setSelectedEmp(emp);
    const toil = leaveTypes.find((lt: any) => lt.code === 'TOIL');
    setForm({ ...form, leaveTypeId: toil?.leaveTypeId || leaveTypes[0]?.leaveTypeId || '' });
    try {
      const overrides = await api.get<any[]>(`/api/admin/leave/employee-overrides?employeeId=${emp.employeeId}&year=${currentYear}`);
      setExisting(overrides);
    } catch { setExisting([]); }
  }

  async function saveEntitlement() {
    if (!selectedEmp || !form.leaveTypeId || form.entitlementDays <= 0) {
      setError('Please select an employee, leave type, and enter days > 0');
      return;
    }
    try {
      setError(''); setSuccess(''); setSaving(true);
      await api.post('/api/admin/leave/employee-overrides', {
        employeeId: selectedEmp.employeeId,
        leaveTypeId: form.leaveTypeId,
        year: form.year,
        entitlementDays: form.entitlementDays,
        notes: form.notes || 'Entitlement adjustment',
      });
      const ltName = leaveTypes.find((lt: any) => lt.leaveTypeId === form.leaveTypeId)?.name || '';
      setSuccess(`${form.entitlementDays} ${ltName} days set for ${selectedEmp.firstName} ${selectedEmp.lastName} (${form.year})`);
      setForm({ ...form, entitlementDays: 0, notes: '' });
      await selectEmployee(selectedEmp);
      await loadAllOverrides();
    } catch (e: any) {
      const body = e.body ? JSON.parse(e.body) : null;
      setError(body?.message || body?.error || e.message || 'Failed to save');
    } finally { setSaving(false); }
  }

  async function deleteOverride(id: string) {
    try {
      setError(''); setSuccess('');
      await api.delete(`/api/admin/leave/employee-overrides/${id}`);
      setSuccess('Override removed');
      if (selectedEmp) await selectEmployee(selectedEmp);
      await loadAllOverrides();
    } catch (e: any) { setError(e.message); }
  }

  if (loading) return <LoadingSpinner />;

  const filtered = search
    ? employees.filter((e: any) => {
        const name = `${e.firstName} ${e.lastName} ${e.employeeNumber}`.toLowerCase();
        return name.includes(search.toLowerCase());
      })
    : [];

  const tableFiltered = filterType
    ? allOverrides.filter((o: any) => {
        const lt = leaveTypes.find((t: any) => t.code === filterType);
        return lt && o.leaveTypeId === lt.leaveTypeId;
      })
    : allOverrides;

  return (
    <div>
      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}
      {success && <div style={{ padding: '10px 16px', marginBottom: 12, background: '#ECFDF5', border: '1px solid #10B981', borderRadius: 6, fontSize: 13, color: '#065F46' }}>{success}</div>}

      <p style={{ fontSize: 13, color: 'var(--gray-500)', marginBottom: 16 }}>
        Use this to set or adjust leave entitlements for individual employees (e.g., credit TOIL days,
        adjust annual leave for pro-rated joiners, or set entitlements for special leave types).
      </p>

      {/* Employee search */}
      <div className={styles.toolBar}>
        <div style={{ flex: 1 }}>
          <input
            className={styles.fieldInput}
            placeholder="Search employee by name or number..."
            value={search}
            onChange={e => { setSearch(e.target.value); setSelectedEmp(null); }}
            style={{ width: '100%' }}
          />
          {search && filtered.length > 0 && !selectedEmp && (
            <Card className={styles.formCard} padding={false}>
              <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                {filtered.slice(0, 10).map((emp: any) => (
                  <div
                    key={emp.employeeId}
                    style={{ padding: '8px 16px', cursor: 'pointer', borderBottom: '1px solid var(--gray-100)' }}
                    onClick={() => { selectEmployee(emp); setSearch(`${emp.firstName} ${emp.lastName} (${emp.employeeNumber})`); }}
                  >
                    <strong>{emp.firstName} {emp.lastName}</strong>{' '}
                    <span style={{ color: 'var(--gray-400)' }}>{emp.employeeNumber} \u2014 {emp.departmentName}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Entry form */}
      {selectedEmp && (
        <Card className={styles.formCard}>
          <h4 className={styles.formTitle}>
            Adjust Entitlement for {selectedEmp.firstName} {selectedEmp.lastName} ({selectedEmp.employeeNumber})
          </h4>

          {existing.length > 0 && (
            <div style={{ marginBottom: 16, padding: 12, background: 'var(--gray-50)', borderRadius: 6, fontSize: 13 }}>
              <strong>Current overrides ({currentYear}):</strong>
              <table style={{ width: '100%', marginTop: 8, fontSize: 13 }}>
                <thead>
                  <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--gray-200)' }}>
                    <th style={{ padding: '4px 8px' }}>Type</th>
                    <th style={{ padding: '4px 8px' }}>Days</th>
                    <th style={{ padding: '4px 8px' }}>Notes</th>
                    <th style={{ padding: '4px 8px' }}>Set By</th>
                    <th style={{ padding: '4px 8px', width: 60 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {existing.map((o: any) => (
                    <tr key={o.employeeLeaveEntitlementId} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                      <td style={{ padding: '4px 8px' }}>{o.leaveTypeName}</td>
                      <td style={{ padding: '4px 8px' }}>{o.entitlementDays}</td>
                      <td style={{ padding: '4px 8px', color: 'var(--gray-400)' }}>{o.notes || '\u2014'}</td>
                      <td style={{ padding: '4px 8px', color: 'var(--gray-400)' }}>{o.setBy}</td>
                      <td style={{ padding: '4px 8px' }}>
                        <button
                          style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', fontSize: 12 }}
                          onClick={() => deleteOverride(o.employeeLeaveEntitlementId)}
                        >Remove</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className={styles.formGrid}>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Leave Type *</label>
              <select className={styles.fieldInput} value={form.leaveTypeId} onChange={e => setForm({ ...form, leaveTypeId: e.target.value })}>
                <option value="">Select...</option>
                {leaveTypes.map((lt: any) => <option key={lt.leaveTypeId} value={lt.leaveTypeId}>{lt.code} \u2014 {lt.name}</option>)}
              </select>
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Year</label>
              <select className={styles.fieldInput} value={form.year} onChange={e => setForm({ ...form, year: Number(e.target.value) })}>
                <option value={currentYear - 1}>{currentYear - 1}</option>
                <option value={currentYear}>{currentYear}</option>
                <option value={currentYear + 1}>{currentYear + 1}</option>
              </select>
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Entitlement Days *</label>
              <input type="number" className={styles.fieldInput} value={form.entitlementDays} onChange={e => setForm({ ...form, entitlementDays: Number(e.target.value) })} min="0" max="365" step="0.5" />
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Notes</label>
              <input className={styles.fieldInput} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="e.g., TOIL credit for weekend work" />
            </div>
          </div>
          <div className={styles.formActions}>
            <button className={styles.cancelBtn} onClick={() => { setSelectedEmp(null); setSearch(''); }}>Cancel</button>
            <button className={styles.saveBtn} onClick={saveEntitlement} disabled={saving}>
              {saving ? 'Saving...' : 'Save Entitlement'}
            </button>
          </div>
        </Card>
      )}

      {/* All overrides table */}
      <Card className={styles.formCard}>
        <h4 className={styles.formTitle}>Entitlement Overrides ({currentYear})</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <label style={{ fontSize: 13, color: 'var(--gray-500)' }}>Filter by type:</label>
          <select className={styles.fieldInput} style={{ width: 200 }} value={filterType} onChange={e => setFilterType(e.target.value)}>
            <option value="">All types</option>
            {leaveTypes.map((lt: any) => <option key={lt.leaveTypeId} value={lt.code}>{lt.code} \u2014 {lt.name}</option>)}
          </select>
          <span style={{ fontSize: 12, color: 'var(--gray-400)' }}>{tableFiltered.length} records</span>
        </div>
        {tableFiltered.length === 0
          ? <p style={{ fontSize: 13, color: 'var(--gray-400)', padding: 16 }}>No overrides found{filterType ? ` for ${filterType}` : ''}.</p>
          : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ textAlign: 'left', borderBottom: '2px solid var(--gray-200)', color: 'var(--gray-500)', textTransform: 'uppercase', fontSize: 11, letterSpacing: '0.05em' }}>
                    <th style={{ padding: '8px' }}>Employee</th>
                    <th style={{ padding: '8px' }}>Leave Type</th>
                    <th style={{ padding: '8px', textAlign: 'right' }}>Days</th>
                    <th style={{ padding: '8px' }}>Notes</th>
                    <th style={{ padding: '8px' }}>Set By</th>
                    <th style={{ padding: '8px' }}>Date</th>
                    <th style={{ padding: '8px', width: 60 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {tableFiltered.map((o: any) => (
                    <tr key={o.employeeLeaveEntitlementId} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                      <td style={{ padding: '8px' }}><strong>{o.employeeName}</strong> <span style={{ color: 'var(--gray-400)' }}>{o.employeeNumber}</span></td>
                      <td style={{ padding: '8px' }}>{o.leaveTypeName}</td>
                      <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>{o.entitlementDays}</td>
                      <td style={{ padding: '8px', color: 'var(--gray-400)' }}>{o.notes || '\u2014'}</td>
                      <td style={{ padding: '8px', color: 'var(--gray-400)' }}>{o.setBy}</td>
                      <td style={{ padding: '8px', color: 'var(--gray-400)' }}>{new Date(o.setAt).toLocaleDateString()}</td>
                      <td style={{ padding: '8px' }}>
                        <button
                          style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', fontSize: 12 }}
                          onClick={() => deleteOverride(o.employeeLeaveEntitlementId)}
                        >Remove</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </Card>
    </div>
  );
}


// =============================================================================
// Extended Calendar Sub-Tab
// =============================================================================

function ExtendedCalendarSubTab() {
  const [employees, setEmployees] = useState<any[]>([]);
  const [entries, setEntries] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(false);
  const [removeId, setRemoveId] = useState<string | null>(null);

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      setLoading(true);
      const [emps, list] = await Promise.all([
        api.get<any[]>('/api/employees'),
        api.get<any[]>('/api/admin/leave/extended-calendar'),
      ]);
      setEmployees(emps);
      setEntries(list);
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }

  async function addEmployee(emp: any) {
    try {
      setError(''); setSuccess(''); setAdding(true);
      await api.post('/api/admin/leave/extended-calendar', { employeeId: emp.employeeId });
      setSuccess(`Extended calendar view granted to ${emp.firstName} ${emp.lastName}`);
      setSearch('');
      await load();
    } catch (e: any) {
      const body = e.body ? JSON.parse(e.body) : null;
      setError(body?.message || e.message || 'Failed to add');
    } finally { setAdding(false); }
  }

  async function removeEmployee(employeeId: string) {
    try {
      setError(''); setSuccess('');
      await api.delete(`/api/admin/leave/extended-calendar/${employeeId}`);
      setSuccess('Extended calendar view removed');
      setRemoveId(null);
      await load();
    } catch (e: any) { setError(e.message); }
  }

  if (loading) return <LoadingSpinner />;

  // Filter search results, exclude employees already on the list
  const entryEmployeeIds = new Set(entries.map((e: any) => e.employeeId));
  const filtered = search
    ? employees.filter((e: any) => {
        if (entryEmployeeIds.has(e.employeeId)) return false;
        const name = `${e.firstName} ${e.lastName} ${e.employeeNumber}`.toLowerCase();
        return name.includes(search.toLowerCase());
      })
    : [];

  return (
    <div>
      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}
      {success && <div style={{ padding: '10px 16px', marginBottom: 12, background: '#ECFDF5', border: '1px solid #10B981', borderRadius: 6, fontSize: 13, color: '#065F46' }}>{success}</div>}

      <p style={{ fontSize: 13, color: 'var(--gray-500)', marginBottom: 16 }}>
        Employees on this list see an extended team calendar &mdash; showing their own leave, their manager&apos;s leave,
        and leave for all employees in their reporting chain (direct reports, their reports, and so on).
      </p>

      {/* Employee search to add */}
      <div className={styles.toolBar}>
        <div style={{ flex: 1 }}>
          <input
            className={styles.fieldInput}
            placeholder="Search employee to add..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: '100%' }}
          />
          {search && filtered.length > 0 && (
            <Card className={styles.formCard} padding={false}>
              <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                {filtered.slice(0, 10).map((emp: any) => (
                  <div
                    key={emp.employeeId}
                    style={{ padding: '8px 16px', cursor: 'pointer', borderBottom: '1px solid var(--gray-100)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    onClick={() => addEmployee(emp)}
                  >
                    <div>
                      <strong>{emp.firstName} {emp.lastName}</strong>{' '}
                      <span style={{ color: 'var(--gray-400)' }}>{emp.employeeNumber} &mdash; {emp.departmentName}</span>
                    </div>
                    <span style={{ fontSize: 12, color: 'var(--turquoise)', fontWeight: 600 }}>+ Add</span>
                  </div>
                ))}
              </div>
            </Card>
          )}
          {search && filtered.length === 0 && !adding && (
            <div style={{ fontSize: 13, color: 'var(--gray-400)', padding: '8px 0' }}>No matching employees found</div>
          )}
        </div>
      </div>

      {/* Current list */}
      <Card className={styles.formCard}>
        <h4 className={styles.formTitle}>Employees with Extended Calendar View</h4>
        {entries.length === 0
          ? <p style={{ fontSize: 13, color: 'var(--gray-400)', padding: 16 }}>No employees configured. Use the search above to add.</p>
          : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ textAlign: 'left', borderBottom: '2px solid var(--gray-200)', color: 'var(--gray-500)', textTransform: 'uppercase', fontSize: 11, letterSpacing: '0.05em' }}>
                    <th style={{ padding: '8px' }}>Employee</th>
                    <th style={{ padding: '8px' }}>Department</th>
                    <th style={{ padding: '8px' }}>Office</th>
                    <th style={{ padding: '8px' }}>Added By</th>
                    <th style={{ padding: '8px' }}>Date Added</th>
                    <th style={{ padding: '8px', width: 80 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry: any) => (
                    <tr key={entry.id} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                      <td style={{ padding: '8px' }}>
                        <strong>{entry.employeeName}</strong>{' '}
                        <span style={{ color: 'var(--gray-400)' }}>{entry.employeeNumber}</span>
                      </td>
                      <td style={{ padding: '8px' }}>{entry.departmentName}</td>
                      <td style={{ padding: '8px' }}>{entry.officeName}</td>
                      <td style={{ padding: '8px', color: 'var(--gray-400)' }}>{entry.createdByName}</td>
                      <td style={{ padding: '8px', color: 'var(--gray-400)' }}>{new Date(entry.createdAt).toLocaleDateString()}</td>
                      <td style={{ padding: '8px' }}>
                        <button
                          style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                          onClick={() => setRemoveId(entry.employeeId)}
                        >Remove</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </Card>

      {/* Remove confirmation */}
      {removeId && (
        <ConfirmDialog
          open={true}
          title="Remove Extended Calendar"
          message="This employee will return to the standard team calendar view (direct reports only). Continue?"
          onConfirm={() => removeEmployee(removeId)}
          onCancel={() => setRemoveId(null)}
        />
      )}
    </div>
  );
}
